import {useNavigate, useParams} from 'react-router-dom';
import styles from './mobileMoney.module.scss';
import backIcon from 'images/back.png';
import {
  Collapse,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Switch,
  Upload,
} from 'antd';
import {useEffect, useState} from 'react';
import imgUpload from 'images/upload_image.png';
import plusIcon from 'images/plus-circle.png';
import deleteIcon from 'images/delete.png';
import {
  mobileMoneySupportedCountries,
  walletCurrencies,
  mobileMoneyOperators,
  mobileMoneyPaymentProviders,
  mobileMoneyGateways,
} from 'utils/mock';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useMutation} from '@apollo/client';
import {UPDATE_MOBILE_MONEY_SETTINGS} from 'graphql/mutations/wallet';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import {
  UploadDocumentToBucket,
  resizeImageFile,
} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import UploadImage from 'components/Startup/profile/companyImage';
import DBModal from 'components/modal/modal';
import MobileMoneyGuide from './guideModal';
import FadeEffect from 'components/animation/fadeEffect';
import trash from 'images/trash-can-outline.svg';
import {fieldRules} from 'utils/helper';

const OPERATORS_FIELD = 'supportedProviders';

const DEFAULT_DEPOSIT_MESSAGE =
  'Veuillez composer le {CODE} pour valider votre depot via {PROVIDER_NAME}';
const DEFAULT_WITHDRAWAL_MESSAGE =
  'Veuillez composer le {CODE} pour valider votre retrait via {PROVIDER_NAME}';

const EMPTY_GUIDE = {description: '', imgURL: '', link: '', videoURL: ''};

/** Only used when creating, or when the API has no provider on record yet. */
const DEFAULT_PROVIDER = 'FLUTTERWAVE';

/** Money values are stored with at most 2 decimal places. */
const toAmount = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return null;
  return Math.round(parsed * 100) / 100;
};

const toInteger = value => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return null;
  return Math.trunc(parsed);
};

const trimmed = value => (typeof value === 'string' ? value.trim() : value);

/** Empty strings must never reach the API — they fail enum/URL validation. */
const orNull = value => {
  const clean = trimmed(value);
  return clean === '' || clean === undefined ? null : clean;
};

const amountFormatter = value => {
  if (value === null || value === undefined || value === '') return '';
  const [wholePart, decimalPart] = `${value}`.split('.');
  const grouped = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimalPart === undefined ? grouped : `${grouped}.${decimalPart}`;
};

const amountParser = value =>
  value === null || value === undefined
    ? value
    : `${value}`.replace(/[^\d.]/g, '');

const amountProps = {
  min: 0,
  step: 0.01,
  precision: 2,
  formatter: amountFormatter,
  parser: amountParser,
};

const fillUssdMessage = (template, code, providerName) => {
  if (!orNull(code) || !orNull(template)) return null;
  return trimmed(template)
    .replace(/{CODE}/g, trimmed(code))
    .replace(/{PROVIDER_NAME}/g, trimmed(providerName) || '');
};

const getCountryCode = key => {
  const country = mobileMoneySupportedCountries.find(item => item.key === key);
  return country ? country.countryCode : null;
};

const emptyOperator = () => ({
  providerCode: undefined,
  providerName: '',
  gateway: undefined,
  countryCode: undefined,
  cashinServiceId: null,
  cashoutServiceId: null,
  providerLogo: '',
  isActive: true,
  directions: [],
  depositCode: '',
  withdrawalCode: '',
  depositDescription: DEFAULT_DEPOSIT_MESSAGE,
  withdrawalDescription: DEFAULT_WITHDRAWAL_MESSAGE,
  minDepositAmount: 0,
  maxDepositAmount: 0,
  minWithdrawalAmount: 0,
  maxWithdrawalAmount: 0,
  depositFee: [],
  depositDiscount: [],
  withdrawalFee: [],
  withdrawalDiscount: [],
  howToGuide: {...EMPTY_GUIDE},
});

const mapFeesToForm = fees =>
  (fees ?? []).map(fee => ({
    feeType: fee?.feeType,
    fixed: fee?.fixedFeeAmount ?? null,
    variable: fee?.variableFeeAmount ?? null,
  }));

/** Only the amount matching the selected fee type is sent, the other is zeroed. */
const mapFeesToPayload = rows =>
  (rows ?? [])
    .filter(row => row?.feeType)
    .map(row => ({
      feeType: row.feeType,
      fixedFeeAmount: row.feeType === 'FIXED' ? toAmount(row?.fixed) ?? 0 : 0,
      variableFeeAmount:
        row.feeType === 'VARIABLE' ? toAmount(row?.variable) ?? 0 : 0,
    }));

/** Drops every key an admin left empty so it is never sent to the API. */
const compact = object =>
  Object.fromEntries(
    Object.entries(object).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  );

const hasKeys = object => Object.keys(object).length > 0;

const MobileMoneySettings = ({updateData}) => {
  const [logoUrl, setLogoUrl] = useState('');
  const [previewLogoUrl, setPreviewLogoUrl] = useState('');
  const [activeKeys, setActiveKeys] = useState([]);
  const [pendingUploads, setPendingUploads] = useState(0);
  const [pendingValues, setPendingValues] = useState(null);
  const [updatePaymentProviderSettings, {loading: loadingUpdate}] = useMutation(
    UPDATE_MOBILE_MONEY_SETTINGS,
    {errorPolicy: 'all'},
  );
  const {code} = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    if (!updateData) return;

    const operators = (updateData?.supportedProviders ?? []).map(provider => ({
      countryCode: provider?.countryCode ?? undefined,
      providerName: provider?.providerName ?? '',
      providerCode: provider?.providerCode ?? undefined,
      gateway: provider?.gateway ?? undefined,
      cashinServiceId: provider?.cashinServiceId ?? null,
      cashoutServiceId: provider?.cashoutServiceId ?? null,
      providerLogo: provider?.providerLogo ?? '',
      isActive: Boolean(provider?.isActive),
      directions: provider?.direction ?? [],
      depositCode: provider?.mobileMoneyDepositUSSDCode ?? '',
      depositDescription:
        provider?.mobileMoneyDepositUSSDCodeDescription ??
        DEFAULT_DEPOSIT_MESSAGE,
      withdrawalCode: provider?.mobileMoneyWithdrawalUSSDCode ?? '',
      withdrawalDescription:
        provider?.mobileMoneyWithdrawalUSSDCodeDescription ??
        DEFAULT_WITHDRAWAL_MESSAGE,
      minDepositAmount: provider?.minDepositAmount ?? null,
      maxDepositAmount: provider?.maxDepositAmount ?? null,
      minWithdrawalAmount: provider?.minWithdrawalAmount ?? null,
      maxWithdrawalAmount: provider?.maxWithdrawalAmount ?? null,
      depositFee: mapFeesToForm(provider?.feeSchedule?.deposit),
      withdrawalFee: mapFeesToForm(provider?.feeSchedule?.withdrawal),
      depositDiscount: mapFeesToForm(provider?.discount?.deposit),
      withdrawalDiscount: mapFeesToForm(provider?.discount?.withdrawal),
      howToGuide: {
        description: provider?.howToGuide?.description ?? '',
        imgURL: provider?.howToGuide?.imgURL ?? '',
        link: provider?.howToGuide?.link ?? '',
        videoURL: provider?.howToGuide?.videoURL ?? '',
      },
    }));

    form.setFieldsValue({
      provider: updateData?.provider ?? DEFAULT_PROVIDER,
      selectedCountry: updateData?.countryName,
      currencyCode: updateData?.currencyCode,
      description: updateData?.subText ?? '',
      [OPERATORS_FIELD]: operators,
    });
    setLogoUrl(updateData?.logo ?? '');
    setPreviewLogoUrl(updateData?.logo ?? '');
    setActiveKeys(operators.map((_, index) => String(index)));
  }, [updateData, form]);

  const buildPayload = values => {
    const fallbackCountryCode = getCountryCode(values?.selectedCountry);

    return {
      provider: values?.provider,
      mobileMoney: [
        compact({
          countryName: values?.selectedCountry,
          currencyCode: values?.currencyCode,
          logo: orNull(logoUrl),
          subText: orNull(values?.description),
          supportedProviders: (values?.[OPERATORS_FIELD] ?? []).map(
            operator => {
              const directions = operator?.directions ?? [];
              const hasDeposit = directions.includes('DEPOSIT');
              const hasWithdrawal = directions.includes('WITHDRAWAL');
              const providerName = trimmed(operator?.providerName);

              const feeSchedule = compact({
                deposit: hasDeposit
                  ? mapFeesToPayload(operator?.depositFee)
                  : null,
                withdrawal: hasWithdrawal
                  ? mapFeesToPayload(operator?.withdrawalFee)
                  : null,
              });
              const discount = compact({
                deposit: hasDeposit
                  ? mapFeesToPayload(operator?.depositDiscount)
                  : null,
                withdrawal: hasWithdrawal
                  ? mapFeesToPayload(operator?.withdrawalDiscount)
                  : null,
              });
              const howToGuide = compact({
                description: orNull(operator?.howToGuide?.description),
                imgURL: orNull(operator?.howToGuide?.imgURL),
                link: orNull(operator?.howToGuide?.link),
                videoURL: orNull(operator?.howToGuide?.videoURL),
              });

              return compact({
                countryCode:
                  orNull(operator?.countryCode) ?? fallbackCountryCode,
                providerName,
                providerCode: operator?.providerCode,
                gateway: operator?.gateway,
                direction: directions.length ? directions : null,
                isActive: Boolean(operator?.isActive),
                providerLogo: orNull(operator?.providerLogo),
                cashinServiceId: toInteger(operator?.cashinServiceId),
                cashoutServiceId: toInteger(operator?.cashoutServiceId),
                minDepositAmount: hasDeposit
                  ? toAmount(operator?.minDepositAmount)
                  : null,
                maxDepositAmount: hasDeposit
                  ? toAmount(operator?.maxDepositAmount)
                  : null,
                minWithdrawalAmount: hasWithdrawal
                  ? toAmount(operator?.minWithdrawalAmount)
                  : null,
                maxWithdrawalAmount: hasWithdrawal
                  ? toAmount(operator?.maxWithdrawalAmount)
                  : null,
                feeSchedule: hasKeys(feeSchedule) ? feeSchedule : null,
                discount: hasKeys(discount) ? discount : null,
                mobileMoneyDepositUSSDCode: hasDeposit
                  ? orNull(operator?.depositCode)
                  : null,
                mobileMoneyDepositUSSDCodeDescription: hasDeposit
                  ? fillUssdMessage(
                      operator?.depositDescription,
                      operator?.depositCode,
                      providerName,
                    )
                  : null,
                mobileMoneyWithdrawalUSSDCode: hasWithdrawal
                  ? orNull(operator?.withdrawalCode)
                  : null,
                mobileMoneyWithdrawalUSSDCodeDescription: hasWithdrawal
                  ? fillUssdMessage(
                      operator?.withdrawalDescription,
                      operator?.withdrawalCode,
                      providerName,
                    )
                  : null,
                howToGuide: hasKeys(howToGuide) ? howToGuide : null,
              });
            },
          ),
        }),
      ],
    };
  };

  /** Runs after the form validates — asks for confirmation before saving. */
  const handleUpdate = values => {
    const operators = values?.[OPERATORS_FIELD] ?? [];

    if (!operators.length) {
      return toast.error('Add at least one mobile money operator.');
    }
    if (pendingUploads > 0) {
      return toast.error('Wait for the logo upload to finish before saving.');
    }

    const duplicate = operators
      .map(operator => operator?.providerCode)
      .find(
        (providerCode, index, list) => list.indexOf(providerCode) !== index,
      );
    if (duplicate) {
      return toast.error(
        `"${duplicate}" is used by more than one operator. Provider codes must be unique.`,
      );
    }

    setPendingValues(values);
  };

  const handleConfirmedUpdate = () => {
    const values = pendingValues;
    if (!values) return;

    updatePaymentProviderSettings({variables: {input: buildPayload(values)}})
      .then(({data, errors}) => {
        const result = data?.updatePaymentProviderSettings;
        if (errors?.length) {
          return toast.error(errors[0].message);
        }
        if (!result) {
          return toast.error('Update failed. Please try again.');
        }
        if (result.__typename === 'Error') {
          return toast.error(result.message);
        }
        toast.success(code ? 'Updated Successfully.' : 'Created Successfully.');
        setPendingValues(null);
        navigate(-1);
      })
      .catch(error => {
        toast.error(error?.message ?? 'Something went wrong.');
      });
  };

  /** Surfaces validation errors hidden inside collapsed operator panels. */
  const handleUpdateFailed = ({errorFields}) => {
    const failedPanels = (errorFields ?? [])
      .filter(field => field?.name?.[0] === OPERATORS_FIELD)
      .map(field => String(field.name[1]));

    if (failedPanels.length) {
      setActiveKeys(previous =>
        previous.concat(failedPanels.filter(key => !previous.includes(key))),
      );
    }
    toast.error('Some fields are missing or invalid. Please review the form.');
  };

  return (
    <div className={styles.root}>
      <div className={styles.nav}>
        <img
          onClick={() => navigate('/dashboard/wallet')}
          src={backIcon}
          alt=""
        />
        <h1>Go to Wallet Overview</h1>
      </div>
      <div className={styles.content}>
        <h1>Mobile Money Settings</h1>
        <UploadImage
          bucket={'momo-operators-logo'}
          previewUrl={previewLogoUrl}
          setImgUrl={setLogoUrl}
          setPreviewUrl={setPreviewLogoUrl}
          label="Upload Country Flag"
        />
        <Form
          form={form}
          onFinish={handleUpdate}
          onFinishFailed={handleUpdateFailed}
          scrollToFirstError
          initialValues={{
            provider: DEFAULT_PROVIDER,
            [OPERATORS_FIELD]: [],
          }}
          layout="vertical">
          <Form.Item
            rules={[{required: true, message: 'This Field is Required'}]}
            label="Payment Provider"
            extra="The provider these mobile money settings are saved under."
            className={styles.field}
            name="provider">
            <Select showSearch optionFilterProp="children">
              {mobileMoneyPaymentProviders.map(item => (
                <Select.Option key={item.key} value={item.key}>
                  {item.value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            rules={[{required: true, message: 'This Field is Required'}]}
            label="Select Country"
            className={styles.field}
            name="selectedCountry">
            <Select showSearch optionFilterProp="children">
              {mobileMoneySupportedCountries.map(item => (
                <Select.Option key={item.key} value={item.key}>
                  {item.value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            rules={[{required: true, message: 'This Field is Required'}]}
            label="Currency Code"
            className={styles.field}
            name="currencyCode">
            <Select showSearch optionFilterProp="children">
              {walletCurrencies.map(item => (
                <Select.Option key={item.key} value={item.key}>
                  {item.key}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea className={styles.field} rows={4} />
          </Form.Item>

          <Form.List name={OPERATORS_FIELD}>
            {(fields, {add, remove}) => (
              <>
                <div className={styles.operatorHeaderRow}>
                  <h2>Supported Mobile Money Operators</h2>
                  {fields.length > 0 && (
                    <div className={styles.collapseControls}>
                      <button
                        type="button"
                        onClick={() =>
                          setActiveKeys(fields.map((_, index) => String(index)))
                        }>
                        Expand all
                      </button>
                      <button type="button" onClick={() => setActiveKeys([])}>
                        Collapse all
                      </button>
                    </div>
                  )}
                </div>
                <Collapse
                  activeKey={activeKeys}
                  onChange={keys => setActiveKeys(keys)}
                  expandIconPosition="end"
                  className={styles.operatorCollapse}>
                  {fields.map(({key, name}) => (
                    <Collapse.Panel
                      key={String(name)}
                      forceRender
                      header={
                        <OperatorHeader
                          form={form}
                          index={name}
                          onRemove={() => {
                            remove(name);
                            setActiveKeys(previous =>
                              previous
                                .filter(item => item !== String(name))
                                .map(item =>
                                  Number(item) > name
                                    ? String(Number(item) - 1)
                                    : item,
                                ),
                            );
                          }}
                        />
                      }>
                      <OperatorPanel
                        key={key}
                        form={form}
                        name={name}
                        onUploadStart={() =>
                          setPendingUploads(count => count + 1)
                        }
                        onUploadEnd={() =>
                          setPendingUploads(count => Math.max(0, count - 1))
                        }
                      />
                    </Collapse.Panel>
                  ))}
                </Collapse>
                <div
                  onClick={() => {
                    add(emptyOperator());
                    setActiveKeys(previous => [
                      ...previous,
                      String(fields.length),
                    ]);
                  }}
                  className={styles['add-button']}>
                  <img src={plusIcon} alt="" />
                  <h2>Add Field</h2>
                </div>
              </>
            )}
          </Form.List>

          <SubmitButton
            disabled={loadingUpdate || pendingUploads > 0}
            label={loadingUpdate ? 'Saving...' : code ? 'Update' : 'Create'}
          />
        </Form>
      </div>
      <DBModal
        isOpen={Boolean(pendingValues)}
        handleClose={() => setPendingValues(null)}
        content={
          <div className={styles.confirmModal}>
            <h1>
              {code
                ? 'Update Mobile Money Settings'
                : 'Create Mobile Money Settings'}
            </h1>
            <p>
              You are about to save{' '}
              <b>{pendingValues?.[OPERATORS_FIELD]?.length ?? 0}</b> operator(s)
              for <b>{pendingValues?.selectedCountry}</b> (
              {pendingValues?.currencyCode}) under the{' '}
              <b>{pendingValues?.provider}</b> provider. Are all the details
              correct?
            </p>
            <div className={styles.confirmButtons}>
              <Button
                onClick={() => setPendingValues(null)}
                type="secondary"
                label="NO"
              />
              <Button
                disabled={loadingUpdate}
                onClick={handleConfirmedUpdate}
                label="YES"
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

const OperatorHeader = ({form, index, onRemove}) => {
  const providerName = Form.useWatch(
    [OPERATORS_FIELD, index, 'providerName'],
    form,
  );

  return (
    <div className={styles.panelHeader}>
      <img
        className={styles.deleteOperator}
        onClick={event => {
          event.stopPropagation();
          onRemove();
        }}
        src={deleteIcon}
        alt="Remove operator"
      />
      <span className={styles.panelTitle}>
        {providerName || `Operator ${index + 1}`}
      </span>
    </div>
  );
};

const OperatorPanel = ({form, name, onUploadStart, onUploadEnd}) => {
  const directions =
    Form.useWatch([OPERATORS_FIELD, name, 'directions'], form) ?? [];
  const selectedCountry = Form.useWatch('selectedCountry', form);
  const hasDeposit = directions.includes('DEPOSIT');
  const hasWithdrawal = directions.includes('WITHDRAWAL');

  // Keep the operator's country code in step with the country picked above,
  // unless an admin deliberately picked a different one.
  useEffect(() => {
    if (!selectedCountry) return;
    const current = form.getFieldValue([OPERATORS_FIELD, name, 'countryCode']);
    if (!current) {
      form.setFieldValue(
        [OPERATORS_FIELD, name, 'countryCode'],
        getCountryCode(selectedCountry),
      );
    }
  }, [selectedCountry, form, name]);

  return (
    <div className={styles.fees}>
      <div className={styles['image-section']}>
        <div>
          <Form.Item name={[name, 'providerLogo']} noStyle>
            <OperatorLogoField
              onUploadStart={onUploadStart}
              onUploadEnd={onUploadEnd}
            />
          </Form.Item>
          <div className={styles.infoModal}>
            <Form.Item name={[name, 'howToGuide']} noStyle>
              <OperatorGuideField index={name} form={form} />
            </Form.Item>
          </div>
        </div>

        <div className={styles.operatorMeta}>
          <div className={styles.fieldGrid}>
            <Form.Item
              name={[name, 'gateway']}
              rules={[{required: true, message: 'This Field is Required'}]}
              label="Gateway">
              <Select placeholder="Select a gateway">
                {mobileMoneyGateways.map(item => (
                  <Select.Option key={item.key} value={item.key}>
                    {item.value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name={[name, 'providerCode']}
              label="Provider Code"
              rules={[{required: true, message: 'This Field is Required'}]}>
              <Select placeholder="Select a provider code">
                {mobileMoneyOperators.map(item => (
                  <Select.Option key={item.key} value={item.key}>
                    {item.value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles.spanTwo}
              name={[name, 'providerName']}
              label="Provider Name"
              rules={[
                {required: true, message: 'This Field is Required'},
                {
                  validator: async (_, value) => {
                    if (value && !String(value).trim()) {
                      throw new Error('Provider Name cannot be blank');
                    }
                  },
                },
              ]}>
              <Input placeholder="e.g. Orange Money" />
            </Form.Item>
            <Form.Item
              name={[name, 'cashinServiceId']}
              label="Cashin Service ID">
              <InputNumber
                precision={0}
                min={0}
                controls={false}
                placeholder="e.g. 1052 (optional)"
                parser={amountParser}
              />
            </Form.Item>
            <Form.Item
              name={[name, 'cashoutServiceId']}
              label="Cashout Service ID">
              <InputNumber
                precision={0}
                min={0}
                controls={false}
                placeholder="e.g. 1053 (optional)"
                parser={amountParser}
              />
            </Form.Item>
            <Form.Item
              name={[name, 'countryCode']}
              rules={[{required: true, message: 'This Field is Required'}]}
              label="Country Code">
              <Select showSearch optionFilterProp="children">
                {mobileMoneySupportedCountries.map(item => (
                  <Select.Option
                    key={item.countryCode}
                    value={item.countryCode}>
                    {item.countryCode}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Active"
              name={[name, 'isActive']}
              valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </div>
      </div>

      <div className={styles.sectionBlock}>
        <hr className={styles.sectionDivider} />
        <h4 className={styles.sectionTitle}>USSD Configuration</h4>
        <div className={styles.fieldGrid}>
          <Form.Item
            className={styles.spanTwo}
            name={[name, 'directions']}
            label="Direction(s)">
            <Select mode="multiple" placeholder="Deposit and/or Withdrawal">
              <Select.Option key="DEPOSIT" value="DEPOSIT">
                Deposit
              </Select.Option>
              <Select.Option key="WITHDRAWAL" value="WITHDRAWAL">
                Withdrawal
              </Select.Option>
            </Select>
          </Form.Item>
          {hasDeposit && (
            <>
              <Form.Item name={[name, 'depositCode']} label="Deposit USSD Code">
                <Input placeholder="*1234#" />
              </Form.Item>
              <Form.Item
                name={[name, 'depositDescription']}
                extra="{CODE} and {PROVIDER_NAME} are replaced on save."
                label="Deposit USSD Message">
                <Input />
              </Form.Item>
            </>
          )}
          {hasWithdrawal && (
            <>
              <Form.Item
                name={[name, 'withdrawalCode']}
                label="Withdrawal USSD Code">
                <Input placeholder="*1234#" />
              </Form.Item>
              <Form.Item
                name={[name, 'withdrawalDescription']}
                extra="{CODE} and {PROVIDER_NAME} are replaced on save."
                label="Withdrawal USSD Message">
                <Input />
              </Form.Item>
            </>
          )}
        </div>

        <div className={styles.feesDropdown}>
          {hasDeposit && (
            <Collapse
              defaultActiveKey={[`deposit_${name}`]}
              className={styles.collapse}>
              <Collapse.Panel
                key={`deposit_${name}`}
                forceRender
                header={<h2 className={styles.collapseHeader}>Deposit</h2>}>
                <DirectionSection
                  form={form}
                  name={name}
                  minField="minDepositAmount"
                  maxField="maxDepositAmount"
                  minLabel="Min. Deposit Amount"
                  maxLabel="Max. Deposit Amount"
                  feeField="depositFee"
                  discountField="depositDiscount"
                  feeButtonText="Add Deposit Fee"
                  discountButtonText="Add Deposit Discount"
                />
              </Collapse.Panel>
            </Collapse>
          )}
          {hasWithdrawal && (
            <Collapse defaultActiveKey={[`withdrawal_${name}`]}>
              <Collapse.Panel
                key={`withdrawal_${name}`}
                forceRender
                header={<h2 className={styles.collapseHeader}>Withdrawal</h2>}>
                <DirectionSection
                  form={form}
                  name={name}
                  minField="minWithdrawalAmount"
                  maxField="maxWithdrawalAmount"
                  minLabel="Min. Withdrawal Amount"
                  maxLabel="Max. Withdrawal Amount"
                  feeField="withdrawalFee"
                  discountField="withdrawalDiscount"
                  feeButtonText="Add Withdrawal Fee"
                  discountButtonText="Add Withdrawal Discount"
                />
              </Collapse.Panel>
            </Collapse>
          )}
        </div>
      </div>
    </div>
  );
};

const DirectionSection = ({
  form,
  name,
  minField,
  maxField,
  minLabel,
  maxLabel,
  feeField,
  discountField,
  feeButtonText,
  discountButtonText,
}) => (
  <div className={styles.scheduleFees}>
    <div className={styles.amountRow}>
      <Form.Item
        name={[name, minField]}
        label={minLabel}
        rules={[{required: true, message: 'This Field is Required'}]}>
        <InputNumber className={styles.minNumber} {...amountProps} />
      </Form.Item>
      <Form.Item
        name={[name, maxField]}
        label={maxLabel}
        dependencies={[[OPERATORS_FIELD, name, minField]]}
        rules={[
          {required: true, message: 'This Field is Required'},
          {
            validator: async (_, value) => {
              const min = form.getFieldValue([OPERATORS_FIELD, name, minField]);
              const bothSet =
                value !== null &&
                value !== undefined &&
                min !== null &&
                min !== undefined;
              if (bothSet && Number(value) < Number(min)) {
                throw new Error('Maximum cannot be lower than the minimum');
              }
            },
          },
        ]}>
        <InputNumber className={styles.minNumber} {...amountProps} />
      </Form.Item>
    </div>

    <div className={styles.multipleFees}>
      <div className={styles.mFee}>
        <h3>Fee Schedule</h3>
        <FeeScheduleInput
          form={form}
          listName={[name, feeField]}
          watchPath={[OPERATORS_FIELD, name, feeField]}
          btnText={feeButtonText}
        />
      </div>
      <div className={styles.mFee}>
        <h3>Discount Schedule</h3>
        <FeeScheduleInput
          form={form}
          listName={[name, discountField]}
          watchPath={[OPERATORS_FIELD, name, discountField]}
          btnText={discountButtonText}
        />
      </div>
    </div>
  </div>
);

const OperatorLogoField = ({value, onChange, onUploadStart, onUploadEnd}) => {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const handleChangeImage = async file => {
    if (!file) return;
    setUploading(true);
    onUploadStart();
    try {
      const resized = await resizeImageFile(file);
      const url = await UploadDocumentToBucket(
        resized,
        setProgress,
        'momo-operators-logo',
      );
      onChange(url);
    } catch (error) {
      toast.error('Failed to upload the operator logo. Please try again.');
    } finally {
      setUploading(false);
      onUploadEnd();
      setProgress(0);
    }
  };

  if (value) {
    return (
      <div className={styles['c-image-uploaded']}>
        <img src={value} alt="" />
        <Button
          className={styles['delete-button']}
          label="Change Image"
          type="secondary"
          onClick={() => onChange('')}
        />
      </div>
    );
  }

  return (
    <Upload
      accept=".png, .jpeg, .jpg"
      name="file"
      showUploadList={false}
      beforeUpload={file => {
        handleChangeImage(file);
        return false;
      }}>
      <div className={styles['c-company-upload']}>
        {uploading ? (
          <div className={styles['loading-spinner']}>
            <h2>{progress}%</h2>
            <Spin size="large" />
            <h2>Loading...</h2>
          </div>
        ) : (
          <>
            <img
              className={styles['company-image']}
              src={imgUpload}
              alt="upload"
            />
            <div style={{marginTop: 6}}>Upload Logo</div>
          </>
        )}
      </div>
    </Upload>
  );
};

const OperatorGuideField = ({value, onChange, index, form}) => {
  const [isOpen, setIsOpen] = useState(false);
  const providerName = Form.useWatch(
    [OPERATORS_FIELD, index, 'providerName'],
    form,
  );

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        label="Open Guide Modal"
        type="secondary"
      />
      <DBModal
        isOpen={isOpen}
        destroyOnClose
        handleClose={() => setIsOpen(false)}
        width={800}
        content={
          <MobileMoneyGuide
            providerName={providerName}
            value={value ?? EMPTY_GUIDE}
            onSave={guide => {
              onChange({...EMPTY_GUIDE, ...guide});
              setIsOpen(false);
            }}
          />
        }
      />
    </>
  );
};

const FeeScheduleInput = ({form, listName, watchPath, btnText}) => {
  const rows = Form.useWatch(watchPath, form);

  const isTypeTaken = (feeType, currentIndex) =>
    (rows ?? []).some(
      (row, index) => index !== currentIndex && row?.feeType === feeType,
    );

  return (
    <Form.List
      name={listName}
      rules={[
        {
          validator: async (_, value) => {
            const types = (value ?? [])
              .map(row => row?.feeType)
              .filter(Boolean);
            const hasDuplicate = types.some(
              (type, index) => types.indexOf(type) !== index,
            );
            if (hasDuplicate) {
              throw new Error('A fee type can only be used once.');
            }
          },
        },
      ]}>
      {(fields, {add, remove}, {errors}) => (
        <div className={styles['fee-list']}>
          {fields.map(({key, name}) => {
            const feeType = rows?.[name]?.feeType;
            return (
              <div key={key} style={{width: '100%'}}>
                <FadeEffect>
                  <div className={styles['fee-item']}>
                    <Form.Item
                      className={styles.feeType}
                      name={[name, 'feeType']}
                      rules={[fieldRules('Fee Type')]}
                      label="Fee Type">
                      <Select
                        placeholder="FIXED"
                        allowClear
                        onChange={selected => {
                          // Drop the amount that no longer applies so a stale
                          // value is never submitted.
                          const clearField =
                            selected === 'VARIABLE' ? 'fixed' : 'variable';
                          form.setFieldValue(
                            [...watchPath, name, clearField],
                            null,
                          );
                        }}>
                        <Select.Option
                          disabled={isTypeTaken('FIXED', name)}
                          key="FIXED"
                          value="FIXED">
                          Fixed
                        </Select.Option>
                        <Select.Option
                          disabled={isTypeTaken('VARIABLE', name)}
                          key="VARIABLE"
                          value="VARIABLE">
                          Variable
                        </Select.Option>
                      </Select>
                    </Form.Item>
                    {feeType === 'VARIABLE' ? (
                      <Form.Item
                        rules={[fieldRules('Variable')]}
                        label="Variable(%)"
                        name={[name, 'variable']}>
                        <InputNumber
                          className={styles.feeNumber}
                          min={0}
                          max={100}
                          step={0.01}
                          precision={2}
                          placeholder="10.50"
                          parser={amountParser}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item
                        label="Fixed"
                        name={[name, 'fixed']}
                        rules={[fieldRules('Fixed')]}>
                        <InputNumber
                          className={styles.feeNumber}
                          placeholder="10.00"
                          {...amountProps}
                        />
                      </Form.Item>
                    )}
                    <img src={trash} onClick={() => remove(name)} alt="" />
                  </div>
                </FadeEffect>
              </div>
            );
          })}
          <Form.ErrorList errors={errors} />
          <div
            onClick={() =>
              add({feeType: undefined, fixed: null, variable: null})
            }
            className={styles['add']}>
            <span>{btnText}</span>
          </div>
        </div>
      )}
    </Form.List>
  );
};

export default MobileMoneySettings;
